/*
 * Render a verification fingerprint (`emoji_hash` bytes) as a short sequence
 * of curated emojis — the human-readable form a user reads aloud to verify
 * their conference is end-to-end-encrypted to the same key on every device.
 *
 * Algorithm: chunks of 8 bytes (clearing the top bit to fit in JS' 53-bit
 * Number safely) are reduced modulo the size of a fixed emoji table. The
 * table is 333 entries — ported as-is from tdesktop's
 * Telegram/SourceFiles/calls/calls_emoji_fingerprint.cpp Data[]/Offsets[]
 * arrays. The output sequence is deterministic for a given input — the
 * point of the protocol — and matches what every other Telegram client
 * displays for the same call, which is THE acceptance test for the e2e
 * crypto port (see Phase 7 in tde2e-port.md).
 *
 * Spec: TdE2E `CallVerificationState::emoji_hash` (32 bytes) → 4 emojis.
 * 1-on-1 fingerprint uses the same algorithm against SHA-256(key + g_a).
 */

// 333-entry emoji list. UTF-16 code units laid out exactly as in tdesktop;
// each emoji is one or more UTF-16 code units, ranges given by the offset
// table below. Encoding the list this way (vs. as a JS string array) keeps
// the source bytes identical to the reference C++ — easy to cross-check.
const EMOJI_CODE_UNITS: number[] = [
  0xd83d, 0xde09, 0xd83d, 0xde0d, 0xd83d, 0xde1b, 0xd83d, 0xde2d, 0xd83d, 0xde31, 0xd83d, 0xde21,
  0xd83d, 0xde0e, 0xd83d, 0xde34, 0xd83d, 0xde35, 0xd83d, 0xde08, 0xd83d, 0xde2c, 0xd83d, 0xde07,
  0xd83d, 0xde0f, 0xd83d, 0xdc6e, 0xd83d, 0xdc77, 0xd83d, 0xdc82, 0xd83d, 0xdc76, 0xd83d, 0xdc68,
  0xd83d, 0xdc69, 0xd83d, 0xdc74, 0xd83d, 0xdc75, 0xd83d, 0xde3b, 0xd83d, 0xde3d, 0xd83d, 0xde40,
  0xd83d, 0xdc7a, 0xd83d, 0xde48, 0xd83d, 0xde49, 0xd83d, 0xde4a, 0xd83d, 0xdc80, 0xd83d, 0xdc7d,
  0xd83d, 0xdca9, 0xd83d, 0xdd25, 0xd83d, 0xdca5, 0xd83d, 0xdca4, 0xd83d, 0xdc42, 0xd83d, 0xdc40,
  0xd83d, 0xdc43, 0xd83d, 0xdc45, 0xd83d, 0xdc44, 0xd83d, 0xdc4d, 0xd83d, 0xdc4e, 0xd83d, 0xdc4c,
  0xd83d, 0xdc4a, 0x270c, 0x270b, 0xd83d, 0xdc50, 0xd83d, 0xdc46, 0xd83d, 0xdc47, 0xd83d, 0xdc49,
  0xd83d, 0xdc48, 0xd83d, 0xde4f, 0xd83d, 0xdc4f, 0xd83d, 0xdcaa, 0xd83d, 0xdeb6, 0xd83c, 0xdfc3,
  0xd83d, 0xdc83, 0xd83d, 0xdc6b, 0xd83d, 0xdc6a, 0xd83d, 0xdc6c, 0xd83d, 0xdc6d, 0xd83d, 0xdc85,
  0xd83c, 0xdfa9, 0xd83d, 0xdc51, 0xd83d, 0xdc52, 0xd83d, 0xdc5f, 0xd83d, 0xdc5e, 0xd83d, 0xdc60,
  0xd83d, 0xdc55, 0xd83d, 0xdc57, 0xd83d, 0xdc56, 0xd83d, 0xdc59, 0xd83d, 0xdc5c, 0xd83d, 0xdc53,
  0xd83c, 0xdf80, 0xd83d, 0xdc84, 0xd83d, 0xdc9b, 0xd83d, 0xdc99, 0xd83d, 0xdc9c, 0xd83d, 0xdc9a,
  0xd83d, 0xdc8d, 0xd83d, 0xdc8e, 0xd83d, 0xdc36, 0xd83d, 0xdc3a, 0xd83d, 0xdc31, 0xd83d, 0xdc2d,
  0xd83d, 0xdc39, 0xd83d, 0xdc30, 0xd83d, 0xdc38, 0xd83d, 0xdc2f, 0xd83d, 0xdc28, 0xd83d, 0xdc3b,
  0xd83d, 0xdc37, 0xd83d, 0xdc2e, 0xd83d, 0xdc17, 0xd83d, 0xdc34, 0xd83d, 0xdc11, 0xd83d, 0xdc18,
  0xd83d, 0xdc3c, 0xd83d, 0xdc27, 0xd83d, 0xdc25, 0xd83d, 0xdc14, 0xd83d, 0xdc0d, 0xd83d, 0xdc22,
  0xd83d, 0xdc1b, 0xd83d, 0xdc1d, 0xd83d, 0xdc1c, 0xd83d, 0xdc1e, 0xd83d, 0xdc0c, 0xd83d, 0xdc19,
  0xd83d, 0xdc1a, 0xd83d, 0xdc1f, 0xd83d, 0xdc2c, 0xd83d, 0xdc0b, 0xd83d, 0xdc10, 0xd83d, 0xdc0a,
  0xd83d, 0xdc2b, 0xd83c, 0xdf40, 0xd83c, 0xdf39, 0xd83c, 0xdf3b, 0xd83c, 0xdf41, 0xd83c, 0xdf3e,
  0xd83c, 0xdf44, 0xd83c, 0xdf35, 0xd83c, 0xdf34, 0xd83c, 0xdf33, 0xd83c, 0xdf1e, 0xd83c, 0xdf1a,
  0xd83c, 0xdf19, 0xd83c, 0xdf0e, 0xd83c, 0xdf0b, 0x26a1, 0x2614, 0x2744, 0x26c4, 0xd83c, 0xdf00,
  0xd83c, 0xdf08, 0xd83c, 0xdf0a, 0xd83c, 0xdf93, 0xd83c, 0xdf86, 0xd83c, 0xdf83, 0xd83d, 0xdc7b,
  0xd83c, 0xdf85, 0xd83c, 0xdf84, 0xd83c, 0xdf81, 0xd83c, 0xdf88, 0xd83d, 0xdd2e, 0xd83c, 0xdfa5,
  0xd83d, 0xdcf7, 0xd83d, 0xdcbf, 0xd83d, 0xdcbb, 0x260e, 0xd83d, 0xdce1, 0xd83d, 0xdcfa, 0xd83d,
  0xdcfb, 0xd83d, 0xdd09, 0xd83d, 0xdd14, 0x23f3, 0x23f0, 0x231a, 0xd83d, 0xdd12, 0xd83d, 0xdd11,
  0xd83d, 0xdd0e, 0xd83d, 0xdca1, 0xd83d, 0xdd26, 0xd83d, 0xdd0c, 0xd83d, 0xdd0b, 0xd83d, 0xdebf,
  0xd83d, 0xdebd, 0xd83d, 0xdd27, 0xd83d, 0xdd28, 0xd83d, 0xdeaa, 0xd83d, 0xdeac, 0xd83d, 0xdca3,
  0xd83d, 0xdd2b, 0xd83d, 0xdd2a, 0xd83d, 0xdc8a, 0xd83d, 0xdc89, 0xd83d, 0xdcb0, 0xd83d, 0xdcb5,
  0xd83d, 0xdcb3, 0x2709, 0xd83d, 0xdceb, 0xd83d, 0xdce6, 0xd83d, 0xdcc5, 0xd83d, 0xdcc1, 0x2702,
  0xd83d, 0xdccc, 0xd83d, 0xdcce, 0x2712, 0x270f, 0xd83d, 0xdcd0, 0xd83d, 0xdcda, 0xd83d, 0xdd2c,
  0xd83d, 0xdd2d, 0xd83c, 0xdfa8, 0xd83c, 0xdfac, 0xd83c, 0xdfa4, 0xd83c, 0xdfa7, 0xd83c, 0xdfb5,
  0xd83c, 0xdfb9, 0xd83c, 0xdfbb, 0xd83c, 0xdfba, 0xd83c, 0xdfb8, 0xd83d, 0xdc7e, 0xd83c, 0xdfae,
  0xd83c, 0xdccf, 0xd83c, 0xdfb2, 0xd83c, 0xdfaf, 0xd83c, 0xdfc8, 0xd83c, 0xdfc0, 0x26bd, 0x26be,
  0xd83c, 0xdfbe, 0xd83c, 0xdfb1, 0xd83c, 0xdfc9, 0xd83c, 0xdfb3, 0xd83c, 0xdfc1, 0xd83c, 0xdfc7,
  0xd83c, 0xdfc6, 0xd83c, 0xdfca, 0xd83c, 0xdfc4, 0x2615, 0xd83c, 0xdf7c, 0xd83c, 0xdf7a, 0xd83c,
  0xdf77, 0xd83c, 0xdf74, 0xd83c, 0xdf55, 0xd83c, 0xdf54, 0xd83c, 0xdf5f, 0xd83c, 0xdf57, 0xd83c,
  0xdf71, 0xd83c, 0xdf5a, 0xd83c, 0xdf5c, 0xd83c, 0xdf61, 0xd83c, 0xdf73, 0xd83c, 0xdf5e, 0xd83c,
  0xdf69, 0xd83c, 0xdf66, 0xd83c, 0xdf82, 0xd83c, 0xdf70, 0xd83c, 0xdf6a, 0xd83c, 0xdf6b, 0xd83c,
  0xdf6d, 0xd83c, 0xdf6f, 0xd83c, 0xdf4e, 0xd83c, 0xdf4f, 0xd83c, 0xdf4a, 0xd83c, 0xdf4b, 0xd83c,
  0xdf52, 0xd83c, 0xdf47, 0xd83c, 0xdf49, 0xd83c, 0xdf53, 0xd83c, 0xdf51, 0xd83c, 0xdf4c, 0xd83c,
  0xdf50, 0xd83c, 0xdf4d, 0xd83c, 0xdf46, 0xd83c, 0xdf45, 0xd83c, 0xdf3d, 0xd83c, 0xdfe1, 0xd83c,
  0xdfe5, 0xd83c, 0xdfe6, 0x26ea, 0xd83c, 0xdff0, 0x26fa, 0xd83c, 0xdfed, 0xd83d, 0xddfb, 0xd83d,
  0xddfd, 0xd83c, 0xdfa0, 0xd83c, 0xdfa1, 0x26f2, 0xd83c, 0xdfa2, 0xd83d, 0xdea2, 0xd83d, 0xdea4,
  0x2693, 0xd83d, 0xde80, 0x2708, 0xd83d, 0xde81, 0xd83d, 0xde82, 0xd83d, 0xde8b, 0xd83d, 0xde8e,
  0xd83d, 0xde8c, 0xd83d, 0xde99, 0xd83d, 0xde97, 0xd83d, 0xde95, 0xd83d, 0xde9b, 0xd83d, 0xdea8,
  0xd83d, 0xde94, 0xd83d, 0xde92, 0xd83d, 0xde91, 0xd83d, 0xdeb2, 0xd83d, 0xdea0, 0xd83d, 0xde9c,
  0xd83d, 0xdea6, 0x26a0, 0xd83d, 0xdea7, 0x26fd, 0xd83c, 0xdfb0, 0xd83d, 0xddff, 0xd83c, 0xdfaa,
  0xd83c, 0xdfad, 0xd83c, 0xddef, 0xd83c, 0xddf5, 0xd83c, 0xddf0, 0xd83c, 0xddf7, 0xd83c, 0xdde9,
  0xd83c, 0xddea, 0xd83c, 0xdde8, 0xd83c, 0xddf3, 0xd83c, 0xddfa, 0xd83c, 0xddf8, 0xd83c, 0xddeb,
  0xd83c, 0xddf7, 0xd83c, 0xddea, 0xd83c, 0xddf8, 0xd83c, 0xddee, 0xd83c, 0xddf9, 0xd83c, 0xddf7,
  0xd83c, 0xddfa, 0xd83c, 0xddec, 0xd83c, 0xdde7, 0x0031, 0x20e3, 0x0032, 0x20e3, 0x0033, 0x20e3,
  0x0034, 0x20e3, 0x0035, 0x20e3, 0x0036, 0x20e3, 0x0037, 0x20e3, 0x0038, 0x20e3, 0x0039, 0x20e3,
  0x0030, 0x20e3, 0xd83d, 0xdd1f, 0x2757, 0x2753, 0x2665, 0x2666, 0xd83d, 0xdcaf, 0xd83d, 0xdd17,
  0xd83d, 0xdd31, 0xd83d, 0xdd34, 0xd83d, 0xdd35, 0xd83d, 0xdd36, 0xd83d, 0xdd37
];

// Offsets[i]..Offsets[i+1] gives the slice of EMOJI_CODE_UNITS that forms
// emoji i. Length kEmojiCount+1.
const OFFSETS: number[] = [
  0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22,
  24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 44, 46,
  48, 50, 52, 54, 56, 58, 60, 62, 64, 66, 68, 70,
  72, 74, 76, 78, 80, 82, 84, 86, 87, 88, 90, 92,
  94, 96, 98, 100, 102, 104, 106, 108, 110, 112, 114, 116,
  118, 120, 122, 124, 126, 128, 130, 132, 134, 136, 138, 140,
  142, 144, 146, 148, 150, 152, 154, 156, 158, 160, 162, 164,
  166, 168, 170, 172, 174, 176, 178, 180, 182, 184, 186, 188,
  190, 192, 194, 196, 198, 200, 202, 204, 206, 208, 210, 212,
  214, 216, 218, 220, 222, 224, 226, 228, 230, 232, 234, 236,
  238, 240, 242, 244, 246, 248, 250, 252, 254, 256, 258, 259,
  260, 261, 262, 264, 266, 268, 270, 272, 274, 276, 278, 280,
  282, 284, 286, 288, 290, 292, 294, 295, 297, 299, 301, 303,
  305, 306, 307, 308, 310, 312, 314, 316, 318, 320, 322, 324,
  326, 328, 330, 332, 334, 336, 338, 340, 342, 344, 346, 348,
  350, 351, 353, 355, 357, 359, 360, 362, 364, 365, 366, 368,
  370, 372, 374, 376, 378, 380, 382, 384, 386, 388, 390, 392,
  394, 396, 398, 400, 402, 404, 406, 407, 408, 410, 412, 414,
  416, 418, 420, 422, 424, 426, 427, 429, 431, 433, 435, 437,
  439, 441, 443, 445, 447, 449, 451, 453, 455, 457, 459, 461,
  463, 465, 467, 469, 471, 473, 475, 477, 479, 481, 483, 485,
  487, 489, 491, 493, 495, 497, 499, 501, 503, 505, 507, 508,
  510, 511, 513, 515, 517, 519, 521, 522, 524, 526, 528, 529,
  531, 532, 534, 536, 538, 540, 542, 544, 546, 548, 550, 552,
  554, 556, 558, 560, 562, 564, 566, 567, 569, 570, 572, 574,
  576, 578, 582, 586, 590, 594, 598, 602, 606, 610, 614, 618,
  620, 622, 624, 626, 628, 630, 632, 634, 636, 638, 640, 641,
  642, 643, 644, 646, 648, 650, 652, 654, 656, 658
];

const EMOJI_COUNT = OFFSETS.length - 1;

// Lazily-built emoji strings — building once is cheap (~333 small strings)
// but we still defer until first use to keep cold start fast.
let emojiTable: string[] | undefined;

function getEmojiTable(): string[] {
  if(emojiTable) return emojiTable;
  const out: string[] = new Array(EMOJI_COUNT);
  for(let i = 0; i < EMOJI_COUNT; i++) {
    const start = OFFSETS[i];
    const end = OFFSETS[i + 1];
    let s = '';
    for(let j = start; j < end; j++) {
      s += String.fromCharCode(EMOJI_CODE_UNITS[j]);
    }
    out[i] = s;
  }
  emojiTable = out;
  return out;
}

// Compute one emoji index from 8 input bytes, big-endian, with the top bit
// cleared (so the result fits comfortably below 2^53 — JS' integer-safe limit).
// Matches `ComputeEmojiIndex` in tdesktop's calls_emoji_fingerprint.cpp.
function emojiIndexFromChunk(bytes: Uint8Array, offset: number): number {
  // Manually combine 8 bytes into a Number. We use plain arithmetic (not
  // bitwise) for the high bytes because bitwise operators in JS work in
  // 32-bit signed integers — multiplying preserves the full precision up to
  // 2^53. The top bit of byte 0 is cleared before assembly.
  const b0 = bytes[offset] & 0x7f;
  const b1 = bytes[offset + 1];
  const b2 = bytes[offset + 2];
  const b3 = bytes[offset + 3];
  const b4 = bytes[offset + 4];
  const b5 = bytes[offset + 5];
  const b6 = bytes[offset + 6];
  const b7 = bytes[offset + 7];
  // Hi 32 bits then lo 32 bits, then assemble.
  const hi = b0 * 0x1000000 + (b1 << 16) + (b2 << 8) + b3;
  const lo = b4 * 0x1000000 + (b5 << 16) + (b6 << 8) + b7;
  // index = hi * 2^32 + lo, mod EMOJI_COUNT (avoid bigint for speed).
  // We use the identity (hi * 2^32 + lo) mod N == (((hi mod N) * (2^32 mod N)) + (lo mod N)) mod N.
  const N = EMOJI_COUNT;
  const POW32_MOD_N = (Math.pow(2, 32) % N);
  return Math.floor(((hi % N) * POW32_MOD_N + (lo % N)) % N);
}

// Render an emoji_hash (32 bytes) as a list of 4 emoji strings.
// Inputs of other lengths are accepted, as long as length is a multiple of 8.
export function emojiFingerprint(emojiHash: Uint8Array): string[] {
  if(emojiHash.length % 8 !== 0) {
    throw new Error(
      `emojiFingerprint: length must be a multiple of 8, got ${emojiHash.length}`
    );
  }
  const table = getEmojiTable();
  const count = emojiHash.length / 8;
  const out: string[] = new Array(count);
  for(let i = 0; i < count; i++) {
    const idx = emojiIndexFromChunk(emojiHash, i * 8);
    out[i] = table[idx];
  }
  return out;
}

// Convenience: 4 emojis joined by a separator (default: zero-width-joiner
// alternative; here just a space so they render as separate glyphs).
export function emojiFingerprintString(emojiHash: Uint8Array, separator = ' '): string {
  return emojiFingerprint(emojiHash).join(separator);
}

// Exposed for tests + UI components that want the raw emoji list.
export function getFingerprintEmojiCount(): number {
  return EMOJI_COUNT;
}
